import { EquipmentConfig, EquipmentStats, EquipmentType, EquipmentRarity, calculateCurrentStats, calculateUpgradeCost } from '../../data/EquipmentData';

export interface EquipmentInstance {
  configId: string;          // ID từ EquipmentData
  instanceId: string;        // Unique instance ID
  currentLevel: number;      // Level hiện tại
  isEquipped: boolean;       // Đang được trang bị
  slotIndex?: number;        // Vị trí slot (cho multiple slots)
}

export class Equipment {
  public config: EquipmentConfig;
  public instance: EquipmentInstance;

  constructor(config: EquipmentConfig, instanceId: string, level: number = 1) {
    this.config = config;
    this.instance = {
      configId: config.id,
      instanceId: instanceId,
      currentLevel: level,
      isEquipped: false,
    };
  }
  getIcon(): string {
    switch (this.config.type) {
      case EquipmentType.WEAPON:
        return '🔫';
      case EquipmentType.ARMOR:
        return '🛡️';
      case EquipmentType.PET:
        return '�';
      case EquipmentType.EARTH:
        return '🌍';
      case EquipmentType.TOWER:
        return '🏗️';
      case EquipmentType.SPECIAL:
        return '💎';
      case EquipmentType.ACCESSORY:
        return '💍';
      default:
        return '🔫';
    }
  }
  // Lấy stats hiện tại dựa trên level
  getCurrentStats(): EquipmentStats {
    return calculateCurrentStats(this.config, this.instance.currentLevel);
  }

  // Kiểm tra có thể nâng cấp không
  canUpgrade(): boolean {
    return this.instance.currentLevel < this.config.maxLevel;
  }

  // Lấy cost nâng cấp
  getUpgradeCost(): number {
    if (!this.canUpgrade()) return 0;
    return calculateUpgradeCost(this.config, this.instance.currentLevel);
  }

  // Nâng cấp
  upgrade(): boolean {
    if (!this.canUpgrade()) return false;
    this.instance.currentLevel++;
    return true;
  }

  // Trang bị
  equip(slotIndex?: number): void {
    this.instance.isEquipped = true;
    this.instance.slotIndex = slotIndex;
  }

  // Bỏ trang bị
  unequip(): void {
    this.instance.isEquipped = false;
    delete this.instance.slotIndex;
  }

  // Lấy tên đầy đủ với level
  getDisplayName(): string {
    return `${this.config.name} +${this.instance.currentLevel - 1}`;
  }

  // Lấy mô tả với stats
  getDetailedDescription(): string {
    const stats = this.getCurrentStats();
    let description = this.config.description + '\n\nStats:\n';
    
    Object.entries(stats).forEach(([key, value]) => {
      if (value !== undefined && value !== 0) {
        description += `${this.formatStatName(key)}: ${this.formatStatValue(key, value)}\n`;
      }
    });
    
    return description;
  }

  private formatStatName(statKey: string): string {
    const statNames: { [key: string]: string } = {
      damage: 'Damage',
      attackSpeed: 'Attack Speed',
      range: 'Range',
      critChance: 'Crit Chance',
      critDamage: 'Crit Damage',
      earthHealth: 'Earth Health',
      earthDefense: 'Earth Defense',
      energyRegen: 'Energy Regen',
      goldBonus: 'Gold Bonus',
      rubyBonus: 'Ruby Bonus',
      xpBonus: 'XP Bonus',
      projectileCount: 'Projectiles',
      pierceCount: 'Pierce Count',
      splashRadius: 'Splash Radius',
      freezeChance: 'Freeze Chance',
      burnChance: 'Burn Chance',
    };
    return statNames[statKey] || statKey;
  }

  private formatStatValue(statKey: string, value: number): string {
    const percentageStats = ['attackSpeed', 'critChance', 'critDamage', 'earthDefense', 'goldBonus', 'rubyBonus', 'xpBonus', 'freezeChance', 'burnChance'];
    
    if (percentageStats.includes(statKey)) {
      return `+${(value * 100).toFixed(1)}%`;
    }
    
    return `+${value.toFixed(0)}`;
  }

  // Serialize để lưu
  serialize(): any {
    return {
      configId: this.config.id,
      instanceId: this.instance.instanceId,
      currentLevel: this.instance.currentLevel,
      isEquipped: this.instance.isEquipped,
      slotIndex: this.instance.slotIndex,
    };
  }

  // Deserialize từ data
  static deserialize(data: any, config: EquipmentConfig): Equipment {
    const equipment = new Equipment(config, data.instanceId, data.currentLevel);
    equipment.instance.isEquipped = data.isEquipped || false;
    equipment.instance.slotIndex = data.slotIndex;
    return equipment;
  }
}

// Equipment Manager để quản lý collection
export class EquipmentManager {
  private equipments: Map<string, Equipment> = new Map();

  // Thêm equipment mới
  addEquipment(equipment: Equipment): void {
    this.equipments.set(equipment.instance.instanceId, equipment);
  }

  // Lấy equipment theo instance ID
  getEquipment(instanceId: string): Equipment | undefined {
    return this.equipments.get(instanceId);
  }

  // Lấy tất cả equipment theo type
  getEquipmentsByType(type: EquipmentType): Equipment[] {
    return Array.from(this.equipments.values()).filter(eq => eq.config.type === type);
  }

  // Lấy equipped equipment theo type
  getEquippedByType(type: EquipmentType): Equipment[] {
    return this.getEquipmentsByType(type).filter(eq => eq.instance.isEquipped);
  }

  // Combine 3 equipment cùng loại thành 1 cấp cao hơn
  combineEquipment(equipment1: Equipment, equipment2: Equipment, equipment3: Equipment): Equipment | null {
    // Kiểm tra cùng config và cùng level
    if (equipment1.config.id !== equipment2.config.id || 
        equipment1.config.id !== equipment3.config.id ||
        equipment1.instance.currentLevel !== equipment2.instance.currentLevel ||
        equipment1.instance.currentLevel !== equipment3.instance.currentLevel) {
      return null;
    }

    // Tạo equipment mới với level cao hơn
    const newLevel = equipment1.instance.currentLevel + 1;
    if (newLevel > equipment1.config.maxLevel) {
      return null; // Không thể combine nữa
    }

    const newInstanceId = `${equipment1.config.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newEquipment = new Equipment(equipment1.config, newInstanceId, newLevel);

    // Xóa 3 equipment cũ
    this.removeEquipment(equipment1.instance.instanceId);
    this.removeEquipment(equipment2.instance.instanceId);
    this.removeEquipment(equipment3.instance.instanceId);

    // Thêm equipment mới
    this.addEquipment(newEquipment);

    return newEquipment;
  }

  // Xóa equipment
  removeEquipment(instanceId: string): boolean {
    return this.equipments.delete(instanceId);
  }

  // Tính tổng stats từ tất cả equipped equipment
  calculateTotalStats(): EquipmentStats {
    const totalStats: EquipmentStats = {};
    
    Array.from(this.equipments.values())
      .filter(eq => eq.instance.isEquipped)
      .forEach(eq => {
        const stats = eq.getCurrentStats();
        Object.entries(stats).forEach(([key, value]) => {
          if (value !== undefined) {
            const statKey = key as keyof EquipmentStats;
            totalStats[statKey] = (totalStats[statKey] || 0) + value;
          }
        });
      });

    return totalStats;
  }

  // Serialize all equipment
  serialize(): any[] {
    return Array.from(this.equipments.values()).map(eq => eq.serialize());
  }

  // Deserialize from data
  deserialize(data: any[], getConfigById: (id: string) => EquipmentConfig | undefined): void {
    this.equipments.clear();
    
    data.forEach(equipData => {
      const config = getConfigById(equipData.configId);
      if (config) {
        const equipment = Equipment.deserialize(equipData, config);
        this.addEquipment(equipment);
      }
    });
  }

  // Generate unique instance ID
  static generateInstanceId(configId: string): string {
    return `${configId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
} 